import { FC, MouseEvent, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import TextInput from "../../components/TextInput/TextInput";
import Button from "../../components/Button/Button";
import Logo from "../../assets/icons/logo.svg";

import { AuthContext } from "../../context/auth";
import { VerifyOtpTypes } from "../../context/types";

import { showErrorToast } from "../../utils/toast";

import "./styles.scss";

const Login: FC = () => {
  let OTPinterval: ReturnType<typeof setInterval>;
  const initialSeconds = 300;

  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [formError, setFormError] = useState("");
  const [linkClickTime, setLinkClickTime] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(initialSeconds);

  const { loading, isAuthenticated, verifyOTP, sendOTP, prospectId, setType } =
    useContext(AuthContext);

  useEffect(() => {
    if (!prospectId) return;

    if (prospectId && !isAuthenticated) {
      setLinkClickTime(new Date().toISOString());
      sendOTP({ prospectId });
      startInterval();
      return;
    }
    if (!prospectId || !isAuthenticated)
      showErrorToast(
        "You are not authenticated. Please open the link again from the SMS"
      );
  }, [prospectId]);

  useEffect(() => {
    if (isAuthenticated && prospectId)
      navigate(`/digital-contract/contract/details`);
    return () => clearInterval(OTPinterval);
  }, []);

  const startInterval = () => {
    OTPinterval = setInterval(() => {
      if (!timeRemaining) {
        setTimeRemaining(initialSeconds);
        return clearInterval(OTPinterval);
      }

      setTimeRemaining((prevSeconds) =>
        prevSeconds > 0 ? prevSeconds - 1 : 0
      );
    }, 1000);
  };

  const handleInputChange = (value: string): void => setValue(value);

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (!value) {
      setFormError("This field is required.");
    } else {
      verifyOTP({
        prospectId,
        otp: value,
        linkClickTime,
        otpEnteredTime: new Date().toISOString(),
      } as VerifyOtpTypes);
    }
  };

  const handleResendAction = (event: MouseEvent) => {
    event?.stopPropagation();
    setType("resend");
    sendOTP({ prospectId } as { prospectId: string });
    startInterval();
  };

  if (loading) return <FullPageLoader />;

  return (
    <div className="main-container">
      <div className="sub-container">
        <div className="d-flex heading-container items-center w-100 justify-center">
          <img src={Logo} alt="logo" />
        </div>
        <div className="welcome-text-container">
          <h5 className="welcome-text">Welcome</h5>
          <h6 className="welcome-sub-text">
            This is a digital contract. Please enter the OTP sent on your
            registered primary phone number.
          </h6>
        </div>
        <TextInput
          showLabel
          required
          onChange={handleInputChange}
          label="Enter OTP"
          placeholder="xxxxxx"
          error={formError}
        />
        <Button
          type="submit"
          subType="primary"
          onClick={handleButtonClick}
          name="Submit"
          className="submit-button"
        />
        <div className="d-flex justify-center w-100 resend-otp-container">
          <h6>
            Didn't receive code?
            <button
              disabled={!!timeRemaining}
              onClick={(event: MouseEvent) => handleResendAction(event)}
            >
              Resend OTP
            </button>
            <p>
              Resend OTP in{" "}
              {Math.floor(timeRemaining / 60)
                .toString()
                .padStart(2, "0")}
              :{(timeRemaining % 60).toString().padStart(2, "0")}
            </p>
          </h6>
        </div>
      </div>
    </div>
  );
};

export default Login;
